/**
 * Auth Interceptor — добавление JWT-токена и обработка 401 с refresh.
 *
 * Важно: запрос refresh идёт через ОТДЕЛЬНЫЙ «голый» axios без интерсепторов.
 * Иначе 401 самого refresh снова попадал бы в этот же обработчик и порождал
 * бесконечный цикл refresh → 401 → refresh (из-за чего страница долбила
 * POST /auth/refresh и не уходила на логин).
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios'
import type { AuthTokens, RefreshTokenResponse } from '@/features/auth/types/auth.types'
import { useToast } from '@/design-system/composables/useToast'
import { i18n } from '@/i18n'

// Extend AxiosRequestConfig type для поддержки _retry флага
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

const REFRESH_PATH = '/api/v1/auth/refresh'
const LOGIN_PATH = '/api/v1/auth/login'
const SECTIONS_PATH = '/api/v1/auth/sections'

// 403 на этих путях — не «права разошлись»: на логине это отказ во входе
// (DEV-1775), а 403 самой карты разделов дал бы цикл 403 → refetch → 403.
const FORBIDDEN_NOT_ABOUT_PERMISSIONS = [LOGIN_PATH, SECTIONS_PATH]

// Точное совпадение пути (без query-строки), чтобы '/auth/login-history'
// и подобные суффиксы случайно не матчились.
const isPath = (url: string | undefined, path: string): boolean => {
  if (!url) {
    return false
  }
  const clean = url.split('?')[0] ?? ''
  return clean === path || clean.endsWith(path)
}

export function setupAuthInterceptor(axiosInstance: AxiosInstance) {
  // «Голый» клиент только под refresh: без request/response интерсепторов,
  // поэтому не несёт протухший Authorization и не зацикливается на своём 401.
  const refreshClient = axios.create({
    baseURL: axiosInstance.defaults.baseURL,
    timeout: axiosInstance.defaults.timeout,
  })

  // Один общий refresh «в полёте»: шквал параллельных 401 при загрузке страницы
  // (/me, /settings, …) не плодит гонку refresh-запросов.
  let refreshPromise: Promise<string> | null = null
  // Защита от многократного редиректа при шквале 401.
  let isRedirecting = false

  const redirectToLogin = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')

    if (isRedirecting) {
      return
    }
    isRedirecting = true

    // Полная перезагрузка на /login гарантированно сбрасывает всё состояние
    // мёртвой сессии. Агентов возвращаем на агентский вход. DEV-232.
    const onAgentArea = window.location.pathname.startsWith('/agents')
    window.location.href = onAgentArea ? '/agents/login' : '/login'
  }

  const persistTokens = (tokens: AuthTokens) => {
    localStorage.setItem('accessToken', tokens.accessToken)
    localStorage.setItem('refreshToken', tokens.refreshToken)

    // Синхронизируем реактивный Pinia-стор (его accessToken читают напрямую,
    // напр. presentation-download). Динамический импорт — чтобы не создавать
    // статическую циклическую зависимость api ↔ store.
    void import('@/features/auth/stores/auth.store')
      .then(({ useAuthStore }) => {
        useAuthStore().setTokens(tokens.accessToken, tokens.refreshToken)
      })
      .catch(() => {
        /* стор ещё не поднят — localStorage уже обновлён, этого достаточно */
      })
  }

  // Single-flight: возвращает новый accessToken либо реджектит, если refresh не удался.
  const refreshTokens = (): Promise<string> => {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Refresh-токен передаётся в отдельном заголовке Refresh-Token (бэкенд
        // читает именно его — см. auth/refresh.go). Тело не используется.
        const { data } = await refreshClient.post<RefreshTokenResponse>(REFRESH_PATH, null, {
          headers: { 'Refresh-Token': `Bearer ${refreshToken}` },
        })

        const tokens = data?.tokens
        if (!tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error('Malformed refresh response')
        }

        persistTokens(tokens)
        return tokens.accessToken
      })().finally(() => {
        refreshPromise = null
      })
    }

    return refreshPromise
  }

  // Один общий перечит карты разделов «в полёте» (DEV-1255): экран, который
  // ходит десятком запросов сразу, при отзыве прав получит десяток 403 —
  // перечитывать карту столько же раз незачем.
  let sectionsRefetch: Promise<unknown> | null = null

  /**
   * 403 означает, что права разошлись с тем, что показывает интерфейс:
   * администратор их только что изменил, а карта разделов в памяти старая.
   * Перечитываем карту и говорим об этом человеку.
   */
  const handleForbidden = (url: string | undefined): void => {
    // Пути не про права — молча наверх: их 403 разбирает вызывающий код.
    // Плюс single-flight ниже: шквал 403 схлопывается в один перечит.
    if (FORBIDDEN_NOT_ABOUT_PERMISSIONS.some((path) => isPath(url, path))) {
      return
    }

    if (sectionsRefetch) {
      return
    }

    useToast().warning(i18n.global.t('common.access.permissionsChanged'))

    // DEV-1496: refetch, а не invalidate — до перечитки интерфейс живёт по
    // прежнему снимку. Права в карте — копия /me, поэтому перечитываем и его:
    // после сброса карта оставалась без прав до перезагрузки страницы.
    sectionsRefetch = Promise.all([
      import('@/features/auth/stores/sections.store').then(({ useSectionsStore }) =>
        useSectionsStore().refetch(),
      ),
      import('@/features/auth/stores/auth.store').then(({ useAuthStore }) =>
        useAuthStore().refreshUserSnapshot(),
      ),
    ])
      .then(() => undefined)
      .catch(() => {
        // Карта не перечиталась — гард переспросит на следующем переходе.
        // Молчим: человек уже увидел тост про изменение прав.
      })
      .finally(() => {
        sectionsRefetch = null
      })
  }

  // Request interceptor — добавление токена
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('accessToken')

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    },
    (error) => Promise.reject(error),
  )

  // Response interceptor — обработка 401 и refresh
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config

      // Строго до проверки на 401 ниже: она отклоняет всё остальное, и после
      // неё эта ветка была бы недостижима.
      if (error.response?.status === 403) {
        handleForbidden(originalRequest?.url)
        return Promise.reject(error)
      }

      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error)
      }

      // 401 на login — это неверные креды; пусть форма входа сама покажет ошибку.
      if (isPath(originalRequest.url, LOGIN_PATH)) {
        return Promise.reject(error)
      }

      // Защитная ветка: 401 самого refresh (если его дёрнули через этот инстанс) —
      // сессия мертва, не зацикливаемся.
      if (isPath(originalRequest.url, REFRESH_PATH)) {
        redirectToLogin()
        return Promise.reject(error)
      }

      // Этот запрос уже ретраили после успешного refresh, а он опять 401 — НЕ
      // выкидываем принудительно из сессии (могла быть частная причина), просто
      // отдаём ошибку наверх.
      if (originalRequest._retry) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      try {
        const newAccessToken = await refreshTokens()

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        return axiosInstance(originalRequest)
      } catch (refreshError) {
        // Refresh не удался — чистим токены и уводим на login (без бесконечных запросов).
        redirectToLogin()
        return Promise.reject(refreshError)
      }
    },
  )
}
